import { defineStore } from 'pinia';
import type { SketchPointObject, PointParametersSnapshot } from '@puppycad/cad-core';
import { EntityInteractionEventType, isSketchPointObject } from '@puppycad/cad-core';
import { BehaviorSubject, type Observable, type Subscription } from 'rxjs';
import { useViewer } from '../useViewer';
import {
  observeViewerEntityInteraction,
  resolveViewerSelectedObject,
} from './viewer-selection-helpers';

/**
 * 点选择状态 Store。
 * 职责：
 * 1. 维护当前选中点对象；
 * 2. 统一派发点选中变化流；
 * 3. 同步画点预览参数与选中点参数。
 */
export const useGetSelectPoint = defineStore('get-select-point', () => {
  let selectedPoint: SketchPointObject | null = null;
  let subscriptions: Subscription[] = [];
  let hasSelectionSubscribed = false;
  let hasDrawingSubscribed = false;
  const pointChanged$ = new BehaviorSubject<SketchPointObject | null>(null);
  /** 统一的点参数流（绘制预览坐标 或 选中点坐标）。 */
  const pointParameters$ = new BehaviorSubject<PointParametersSnapshot>({ x: null, y: null });
  const { getViewer } = useViewer();

  

  /**
   * 解析当前可用的草图绘制管理器。
   * @returns 草图绘制管理器；未就绪时返回 null。
   */
  function resolvePointDrawingParameters(): Observable<PointParametersSnapshot> | null {
    return getViewer()?.observePointDrawingParameters?.() as Observable<PointParametersSnapshot> | null;
  }

  /**
   * 从选中点对象读取参数快照。
   * @param point 点对象，null 时返回空参数。
   * @returns 点参数快照。
   */
  function snapshotFromPoint(point: SketchPointObject | null): PointParametersSnapshot {
    if (!point) {
      return { x: null, y: null };
    }
    return {
      x: Number.isFinite(point.x) ? point.x : null,
      y: Number.isFinite(point.y) ? point.y : null,
    };
  }

  /**
   * 从实体选择管理器同步当前选中点。
   */
  function syncFromManager(): void {
    selectedPoint = resolveViewerSelectedObject(getViewer(), isSketchPointObject);
    pointChanged$.next(selectedPoint);
    pointParameters$.next(snapshotFromPoint(selectedPoint));
  }

  /**
   * 处理点选中事件。
   */
  function handlePointSelected(): void {
    syncFromManager();
  }

  /**
   * 处理选择清空事件。
   */
  function handleSelectionCleared(): void {
    selectedPoint = null;
    pointChanged$.next(null);
    pointParameters$.next({ x: null, y: null });
  }

  /**
   * 尝试绑定点选择事件。
   * @returns 是否绑定成功。
   */
  function subscribe(): void {
    const viewer = getViewer();
    const pointSelected$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.PointSelected);
    const selectionCleared$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionCleared);
    const pointDrawingParameters$ = resolvePointDrawingParameters();
    if (!pointSelected$ || !selectionCleared$ || !pointDrawingParameters$) {
      return;
    }
    if (hasSelectionSubscribed === false) {
      syncFromManager();
      subscriptions.push(
        pointSelected$.subscribe(handlePointSelected),
        selectionCleared$.subscribe(handleSelectionCleared),
      );
      hasSelectionSubscribed = true;
    }
    if (hasDrawingSubscribed === false) {
      subscriptions.push(
        pointDrawingParameters$.subscribe((snapshot: PointParametersSnapshot) => {
          if (snapshot.x !== null && snapshot.y !== null) {
            pointParameters$.next(snapshot);
            return;
          }
          pointParameters$.next(snapshotFromPoint(selectedPoint));
        }),
      );
      hasDrawingSubscribed = true;
    }
  }

  /**
   * 取消点选择与画点参数事件监听。
   */
  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSelectionSubscribed = false;
    hasDrawingSubscribed = false;
  }

  /**
   * 显式同步当前选中点与参数。
   */
  function syncCurrentSelection(): void {
    syncFromManager();
  }

  return {
    getSelectedPoint: (): SketchPointObject | null => selectedPoint,
    onPointChanged: (): Observable<SketchPointObject | null> => pointChanged$.asObservable(),
    onPointParametersChanged: (): Observable<PointParametersSnapshot> => pointParameters$.asObservable(),
    subscribe,
    unsubscribe,
    syncCurrentSelection,
  };
});
